<template>
  <div class="analysis-container p-16">
    <el-form ref="form" label-position="top" :inline="false" class="full-height">
      <el-card class="mb-16 shadow-sm">
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item :label="$t('Curve')">
              <el-select v-model="form.instrumentGroup" filterable @change="handleInstrumentGroupChange" class="w-100">
                <el-option v-for="item in displayGroupList" :key="item._id" :value="item._id" :label="item.name" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="16">
            <el-form-item :label="$t('datetime')">
              <div class="flex-row align-center wrap">
                <template v-if="projectId && dateSelectionMode === 'specific'">
                  <el-select
                    v-model="form.date"
                    multiple
                    collapse-tags
                    placeholder="請選擇有數據的時間點 (可複選)"
                    @change="getInstrumentGroupOutputByTime"
                    class="mr-16"
                    style="flex: 1; min-width: 250px;"
                    v-loading="loadingTimestamps"
                  >
                    <el-option
                      v-for="item in groupTimestampList"
                      :key="item.value"
                      :label="item.label"
                      :value="item.value"
                    />
                  </el-select>
                </template>

                <el-radio-group v-model="dateSelectionMode" size="small" @change="handleModeChange" class="mr-16">
                  <el-radio-button label="specific">{{ $t('Specific Dates') }}</el-radio-button>
                  <el-radio-button label="range">{{ $t('Date Range') }}</el-radio-button>
                </el-radio-group>

                <template v-if="dateSelectionMode === 'specific' && !projectId">
                  <div v-for="(date, index) in form.date" :key="index" class="mr-8 mb-8">
                    <el-date-picker v-model="form.date[index]" type="datetime" format="yyyy-MM-dd HH:mm"
                      value-format="timestamp" :picker-options="datePickerOptions" size="small"
                      @change="getInstrumentGroupOutputByTime" style="width: 180px" />
                  </div>
                  <el-button type="primary" icon="el-icon-plus" circle size="mini" @click="onAddNewDate" class="mr-8" />
                  <el-button size="mini" @click="addSeven">{{ $t('chart.addSeven') }}</el-button>
                </template>

                <el-date-picker v-else-if="dateSelectionMode === 'range'" v-model="form.dateRange" type="datetimerange" size="small"
                  range-separator="-" :start-placeholder="$t('Start')" :end-placeholder="$t('End')"
                  :picker-options="rangePickerOptions" value-format="timestamp" format="yyyy-MM-dd HH:mm"
                  @change="getInstrumentGroupOutputByTime" />
              </div>
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider class="my-12" />

        <div class="flex-row justify-between align-center">
          <div class="checkbox-group">
            <el-checkbox v-model="checked" @change="handleChange">{{ $t('chart.table') }}</el-checkbox>
            <el-checkbox v-model="autoScale" @change="handleChange" class="ml-16">{{ $t('chart.autoScale') }}</el-checkbox>
          </div>
          <div>
            <el-button type="success" icon="el-icon-download" size="small" @click="exportChartImage">輸出圖像</el-button>
          </div>
        </div>
      </el-card>

      <div v-if="hasData">
        <template v-if="projectId && isMergedGroup">
          <el-row :gutter="20">
            <el-col :span="12">
              <el-card class="chart-card shadow-sm mb-16" :body-style="{ padding: '10px' }">
                <div slot="header"><span>{{ currentSelectedGroup.name }} - X軸圖表</span></div>
                <div :style="chartContainerStyle">
                  <v-chart ref="chartX" style="width:100%; height:100%;" :autoresize="true" :options="chartOptionsX" />
                </div>
              </el-card>
              
              <el-card class="mb-16 shadow-sm settings-panel">
                <div slot="header" class="clearfix"><span>X軸 座標軸刻度自定義</span></div>
                <el-row :gutter="10">
                  <el-col :span="12">
                    <p class="setting-label">X-Axis Min/Max</p>
                    <el-input-number v-model="mergedAxes.x.xAxis.min" size="mini" class="w-100 mb-8" />
                    <el-input-number v-model="mergedAxes.x.xAxis.max" size="mini" class="w-100" />
                  </el-col>
                  <el-col :span="12">
                    <p class="setting-label">Y-Axis Min/Max</p>
                    <el-input-number v-model="mergedAxes.x.yAxis.min" size="mini" class="w-100 mb-8" />
                    <el-input-number v-model="mergedAxes.x.yAxis.max" size="mini" class="w-100" />
                  </el-col>
                </el-row>
              </el-card>
            </el-col>

            <el-col :span="12">
              <el-card class="chart-card shadow-sm mb-16" :body-style="{ padding: '10px' }">
                <div slot="header"><span>{{ currentSelectedGroup.name }} - Y軸圖表</span></div>
                <div :style="chartContainerStyle">
                  <v-chart ref="chartY" style="width:100%; height:100%;" :autoresize="true" :options="chartOptionsY" />
                </div>
              </el-card>

              <el-card class="mb-16 shadow-sm settings-panel">
                <div slot="header" class="clearfix"><span>Y軸 座標軸刻度自定義</span></div>
                <el-row :gutter="10">
                  <el-col :span="12">
                    <p class="setting-label">X-Axis Min/Max</p>
                    <el-input-number v-model="mergedAxes.y.xAxis.min" size="mini" class="w-100 mb-8" />
                    <el-input-number v-model="mergedAxes.y.xAxis.max" size="mini" class="w-100" />
                  </el-col>
                  <el-col :span="12">
                    <p class="setting-label">Y-Axis Min/Max</p>
                    <el-input-number v-model="mergedAxes.y.yAxis.min" size="mini" class="w-100 mb-8" />
                    <el-input-number v-model="mergedAxes.y.yAxis.max" size="mini" class="w-100" />
                  </el-col>
                </el-row>
              </el-card>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :span="24">
              <el-card class="mb-16 shadow-sm">
                <div slot="header" class="clearfix"><span>{{ $t('Compare') }}</span></div>
                <div class="flex-row align-center">
                  <el-button :type="computeFlag ? 'warning' : 'default'" size="small" @click="activeCompute" class="mr-8" :loading="loading">
                    {{ computeFlag ? '取消比較' : '啟用比較' }}
                  </el-button>
                  <el-date-picker v-model="computeDate" type="datetime" size="small" format="yyyy-MM-dd HH:mm" 
                    value-format="timestamp" @change="compute" placeholder="選擇比較基準點" />
                </div>
              </el-card>

              <el-card v-if="showTable" class="shadow-sm">
                <div slot="header" class="flex-row justify-between align-center">
                  <span>數據報表</span>
                  <el-button type="primary" size="mini" plain @click="exportToExcel">Export Excel</el-button>
                </div>
                <hot-table :settings="hotSettings" ref="hotTable" class="modern-hot-table" />
              </el-card>
            </el-col>
          </el-row>
        </template>

        <template v-else>
          <el-row :gutter="20">
            <el-col :md="currentInstrumentGroup.type === 'VERTICAL_DISTRIBUTION' ? 8 : 16">
              <el-card class="chart-card shadow-sm" :body-style="{ padding: '10px' }">
                <div :style="chartContainerStyle">
                  <v-chart ref="chart" style="width:100%; height:100%;" :autoresize="true" :options="chartOptions" />
                </div>
              </el-card>
            </el-col>

            <el-col :md="currentInstrumentGroup.type === 'VERTICAL_DISTRIBUTION' ? 16 : 8">
              <el-card class="mb-16 shadow-sm">
                <div slot="header" class="clearfix"><span>{{ $t('Compare') }}</span></div>
                <div class="flex-row align-center">
                  <el-button :type="computeFlag ? 'warning' : 'default'" size="small" @click="activeCompute" class="mr-8" :loading="loading">
                    {{ computeFlag ? '取消比較' : '啟用比較' }}
                  </el-button>
                  <el-date-picker v-model="computeDate" type="datetime" size="small" format="yyyy-MM-dd HH:mm" 
                    value-format="timestamp" @change="compute" placeholder="選擇比較基準點" />
                </div>
              </el-card>

              <el-card class="mb-16 shadow-sm settings-panel">
                <div slot="header" class="clearfix"><span>座標軸刻度自定義</span></div>
                <el-row :gutter="10">
                  <el-col :span="12">
                    <p class="setting-label">X-Axis Min/Max</p>
                    <el-input-number v-model="currentInstrumentGroup.xAxis.min" size="mini" class="w-100 mb-8" />
                    <el-input-number v-model="currentInstrumentGroup.xAxis.max" size="mini" class="w-100" />
                  </el-col>
                  <el-col :span="12">
                    <p class="setting-label">Y-Axis Min/Max</p>
                    <el-input-number v-model="currentInstrumentGroup.yAxis.min" size="mini" class="w-100 mb-8" />
                    <el-input-number v-model="currentInstrumentGroup.yAxis.max" size="mini" class="w-100" />
                  </el-col>
                </el-row>
              </el-card>

              <el-card v-if="showTable" class="shadow-sm">
                <div slot="header" class="flex-row justify-between align-center">
                  <span>數據報表</span>
                  <el-button type="primary" size="mini" plain @click="exportToExcel">Export Excel</el-button>
                </div>
                <hot-table :settings="hotSettings" ref="hotTable" class="modern-hot-table" />
              </el-card>
            </el-col>
          </el-row>
        </template>
      </div>

      <el-card v-else class="text-center p-40">
        <i class="el-icon-info" style="font-size: 48px; color: #C0C4CC"></i>
        <p style="color: #909399; margin-top: 16px;">請選擇儀器群組與時間以顯示圖表</p>
      </el-card>
    </el-form>
  </div>
</template>

<script>
import moment from 'moment';
import { debounce } from 'lodash';
import { HotTable } from '@handsontable/vue';
import * as XLSX from 'xlsx';

export default {
  components: {
    HotTable,
  },
  props: {
    projectId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      loadingTimestamps: false,
      hotSettings: {
        data: [],
        columns: [],
        colHeaders: [],
        licenseKey: 'non-commercial-and-evaluation',
        colWidths: 150,
        height: 400,
        readOnly: true,
      },
      dateSelectionMode: 'specific', // 'specific' | 'range'
      form: {
        instrumentGroup: '',
        date: [],
        dateRange: [],
      },
      groupTimestampList: [],
      datePickerOptions: {
        disabledDate(date) {
          return date > new Date();
        },
      },
      checked: false,
      chartContainerWidth: null,
      outputList: [],       // 用於單軸/原始架構資料
      oriOutputList: [],
      outputListX: [],      // 合併架構下：X軸資料
      oriOutputListX: [],
      outputListY: [],      // 合併架構下：Y軸資料
      oriOutputListY: [],
      instrumentGroupList: [],
      chartContainerStyle: { width: '100%', height: '500px' },
      computeDate: null,
      computeFlag: false,
      showTable: false,
      tableData: [],
      autoScale: false,

      // 用於暫存合併型儀器自定義座標軸數值
      mergedAxes: {
        x: { xAxis: { min: null, max: null }, yAxis: { min: null, max: null } },
        y: { xAxis: { min: null, max: null }, yAxis: { min: null, max: null } },
      },
    };
  },
  computed: {
    selectedProject() {
      return this.$store.getters['project/selectedProject'];
    },
    activeProjectId() {
      return this.projectId || this.selectedProject._id;
    },
    // 過濾、合併後的儀器群組選單清單
    displayGroupList() {
      if (!this.projectId) return this.instrumentGroupList;

      const mergedMap = new Map();
      const singles = [];

      this.instrumentGroupList.forEach(group => {
        // 利用正規表達式匹配名稱結尾為 -X, _X, (X) 或 Y 的項目
        const match = group.name.match(/^(.*?)[- ]?([XYxy])\)?$/);
        if (match) {
          const baseName = match[1].trim();
          const axis = match[2].toUpperCase();
          if (!mergedMap.has(baseName)) {
            mergedMap.set(baseName, { X: null, Y: null });
          }
          mergedMap.get(baseName)[axis] = group;
        } else {
          singles.push(group);
        }
      });

      const mergedList = [];
      mergedMap.forEach((axes, baseName) => {
        if (axes.X && axes.Y) {
          // 如果同時存在 X 與 Y 軸，將其合併為單一虛擬群組項
          mergedList.push({
            _id: `merged_${axes.X._id}_${axes.Y._id}`,
            name: baseName,
            isMerged: true,
            xGroup: axes.X,
            yGroup: axes.Y,
          });
        } else {
          if (axes.X) singles.push(axes.X);
          if (axes.Y) singles.push(axes.Y);
        }
      });

      return [...mergedList, ...singles];
    },
    currentSelectedGroup() {
      return this.displayGroupList.find(g => g._id === this.form.instrumentGroup);
    },
    isMergedGroup() {
      return this.currentSelectedGroup?.isMerged || false;
    },
    currentInstrumentGroup() {
      if (this.isMergedGroup) {
        return this.currentSelectedGroup.xGroup; 
      }
      const group = this.instrumentGroupList.find(ele => ele._id === this.form.instrumentGroup);
      return group || { 
        name: '', type: '', 
        xAxis: { unit: '', min: null, max: null }, 
        yAxis: { unit: '', min: null, max: null },
      };
    },
    hasData() {
      if (this.projectId && this.isMergedGroup) {
        return this.outputListX.length > 0 || this.outputListY.length > 0;
      }
      return this.outputList.length > 0;
    },
    formattedDateList() {
      return this.form.date.map(timestamp => moment(Number(timestamp)).format('YYYY/MM/DD HH:mm'));
    },
    // 以下為個別產生 X 軸、Y 軸與單軸通用 Series 核心
    series() {
      return this.generateSeries(this.outputList, this.currentInstrumentGroup);
    },
    seriesX() {
      return this.generateSeries(this.outputListX, this.currentSelectedGroup.xGroup);
    },
    seriesY() {
      return this.generateSeries(this.outputListY, this.currentSelectedGroup.yGroup);
    },
    // 各圖表 ECharts 配置項
    chartOptions() {
      return this.buildChartOptions(this.currentInstrumentGroup.name, this.series, this.currentInstrumentGroup);
    },
    chartOptionsX() {
      return this.buildChartOptions(`${this.currentSelectedGroup.name}-X`, this.seriesX, this.currentSelectedGroup.xGroup, 'x');
    },
    chartOptionsY() {
      return this.buildChartOptions(`${this.currentSelectedGroup.name}-Y`, this.seriesY, this.currentSelectedGroup.yGroup, 'y');
    },
  },
  mounted() {
    if (this.projectId) {
      console.log('Project ID:', this.projectId);
    } else {
      this.form.date = [Number(moment().startOf('day').format('x'))];
    }
    this.getInstrumentGroupList();
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  watch: {
    'currentInstrumentGroup.type'() {
      this.handleResize();
    },
    'outputList': { handler() { this.handleChange(); }, deep: true },
    'outputListX': { handler() { this.handleChange(); }, deep: true },
    'outputListY': { handler() { this.handleChange(); }, deep: true },
    'form.date'(newValue) {
      const nullIndex = newValue.findIndex(timestamp => timestamp == null);
      if (nullIndex !== -1) this.form.date.splice(nullIndex, 1);
      this.resizeFunc();
    },
  },
  methods: {
    handleResize: debounce(function() {
      const vh = window.innerHeight;
      const isVertical = this.currentInstrumentGroup.type === 'VERTICAL_DISTRIBUTION';
      this.chartContainerStyle = {
        width: '100%',
        height: isVertical ? `${vh * 0.6}px` : '400px',
        minHeight: '350px',
      };
    }, 200),
    async handleInstrumentGroupChange() {
      if (this.dateSelectionMode !== 'specific' || this.projectId) {
        this.form.date = [];
      }
      this.outputList = []; this.oriOutputList = [];
      this.outputListX = []; this.oriOutputListX = [];
      this.outputListY = []; this.oriOutputListY = [];
      
      if (!this.form.instrumentGroup) return;

      if (this.projectId) {
        this.loadingTimestamps = true;
        try {
          // 如果是合併型，取 X 軸的群組 ID 去拉取時間清單
          const targetGroupId = this.isMergedGroup 
            ? this.currentSelectedGroup.xGroup._id 
            : this.form.instrumentGroup;

          const res = await this.$axios({
            category: 'manual',
            method: 'get',
            apiName: `getInstrumentGroupTimestampList/${this.activeProjectId}/${targetGroupId}`,
          });
          
          this.groupTimestampList = [...res.data]
            .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
            .map(t => {
              const ts = new Date(t).getTime();
              return { value: ts, label: moment(ts).format('YYYY-MM-DD HH:mm') };
            });

          // 初始化合併座標軸資料結構
          if (this.isMergedGroup) {
            this.mergedAxes.x = this.$lodash.cloneDeep(this.currentSelectedGroup.xGroup);
            this.mergedAxes.y = this.$lodash.cloneDeep(this.currentSelectedGroup.yGroup);
          }
        } catch (err) {
          console.error(err);
          this.$message.error('無法取得歷史時間清單');
        } finally {
          this.loadingTimestamps = false;
        }
      } else {
        if (this.dateSelectionMode === 'specific' && (!this.form.date || this.form.date.length === 0)) {
          this.form.date = [Number(moment().startOf('day').format('x'))];
        }
        this.getInstrumentGroupOutputByTime();
      }
    },
    handleModeChange(val) {
      this.outputList = []; this.oriOutputList = [];
      this.outputListX = []; this.outputListY = [];
      this.computeFlag = false;
      this.computeDate = null;
      
      if (val === 'range') {
        this.form.dateRange = [Number(moment().subtract(1, 'hour').format('x')), Number(moment().format('x'))];
        if (!this.projectId) this.getInstrumentGroupOutputByTime();
      } else {
        this.form.date = this.projectId ? [] : [Number(moment().startOf('day').format('x'))];
        if (!this.projectId) this.getInstrumentGroupOutputByTime();
      }
    },
    generateSeries(outputList, targetGroup) {
      if (!outputList || outputList.length === 0) return [];
      return this.form.date.map((timestamp, index) => {
        const seriesData = outputList.reduce((acc, curr) => {
          if (curr.output[timestamp] != null) {
            if (targetGroup.type === 'HORIZONTAL_DISTRIBUTION') {
              acc.push([curr.depth, curr.output[timestamp], moment(Number(timestamp)).format('YYYY/MM/DD HH:mm')]);
            } else {
              acc.push([curr.output[timestamp], curr.depth, moment(Number(timestamp)).format('YYYY/MM/DD HH:mm')]);
            }
          }
          return acc;
        }, []);
        return {
          name: this.formattedDateList[index],
          type: 'line', smooth: true,
          lineStyle: { width: 3 },
          data: seriesData,
        };
      });
    },
    buildChartOptions(title, targetSeries, targetGroup, mergeKey = null) {
      const vm = this;
      const number = targetSeries.length;
      
      // 根據是單獨、還是合併X/Y軸，取得正確的自定義座標配置項
      let currentGroupConfig = targetGroup;
      if (this.projectId && this.isMergedGroup && mergeKey) {
        currentGroupConfig = this.mergedAxes[mergeKey];
      }

      const buildValueAxis = (axisType) => {
        const axisKey = targetGroup.type === 'HORIZONTAL_DISTRIBUTION' ? 'yAxis' : 'xAxis';
        return {
          type: 'value', axisLine: { onZero: false }, position: 'top',
          axisLabel: { formatter: `{value} ${targetGroup[axisKey].unit}` },
          min: vm.autoScale ? null : currentGroupConfig[axisKey].min,
          max: vm.autoScale ? null : currentGroupConfig[axisKey].max,
          boundaryGap: false,
        };
      };

      const buildCategoryAxis = () => {
        const axisKey = targetGroup.type === 'HORIZONTAL_DISTRIBUTION' ? 'xAxis' : 'yAxis';
        return {
          type: 'value', axisLine: { onZero: false },
          axisLabel: { formatter: `{value} ${targetGroup[axisKey].unit}` },
          min: currentGroupConfig[axisKey].min, max: currentGroupConfig[axisKey].max,
          boundaryGap: false,
        };
      };

      return {
        title: { text: title, left: 'center', padding: 15 },
        legend: {
          type: 'plain', orient: 'horizontal', x: 'center', y: 'bottom', left: 10,
          itemHeight: number > 8 ? 10 : 14,
          textStyle: { fontSize: number > 8 ? 11 : 12 },
          formatter(label) {
            let date = new Date(label);
            return (date.getMinutes() == 0 && date.getHours() == 0) ? moment(date).format('yyyy/MM/DD') : label;
          },
        },
        tooltip: {
          trigger: 'item',
          formatter(params) {
            let [val1, val2, datetime] = params.data;
            let depth = targetGroup.type === 'HORIZONTAL_DISTRIBUTION' ? val1 : val2;
            let output = targetGroup.type === 'HORIZONTAL_DISTRIBUTION' ? val2 : val1;
            const yLabel = targetGroup.type === 'HORIZONTAL_DISTRIBUTION' ? vm.$t('Distance') : vm.$t('Depth');
            return `${datetime}<br>${yLabel}: ${depth}<br>${vm.$t('Value')}: ${output}`;
          },
        },
        series: targetSeries,
        dataZoom: [{ bottom: '0%', type: 'inside' }],
        xAxis: targetGroup.type === 'HORIZONTAL_DISTRIBUTION' ? buildCategoryAxis() : buildValueAxis(),
        yAxis: targetGroup.type === 'HORIZONTAL_DISTRIBUTION' ? buildValueAxis() : buildCategoryAxis(),
        grid: { left: 60, right: 60, bottom: 85 + (Math.floor(number / 5) * 20) },
        backgroundColor: '#FFFFFF',
      };
    },
    // 獲取數據入口
    async getInstrumentGroupOutputByTime() {
      if (!this.form.instrumentGroup) return;

      if (this.projectId && this.isMergedGroup) {
        // 合併模式下同時發送 X 與 Y 軸的資料提取
        await Promise.all([
          this.fetchDataPromise(this.currentSelectedGroup.xGroup._id, 'x'),
          this.fetchDataPromise(this.currentSelectedGroup.yGroup._id, 'y'),
        ]);
        this.compute();
      } else {
        await this.fetchDataPromise(this.form.instrumentGroup, 'normal');
        this.compute();
      }
    },
    async fetchDataPromise(groupId, typeKey) {
      if (this.dateSelectionMode === 'range') {
        if (!this.form.dateRange || this.form.dateRange.length < 2) return;
        const res = await this.$axios({
          category: 'project', method: 'post',
          apiName: `${this.activeProjectId}/instrument-group/${groupId}/output/range`,
          data: { start: this.form.dateRange[0], end: this.form.dateRange[1] },
        });
        const tempOutputList = res.data.sort((a, b) => a.depth - b.depth);
        let timestamps = tempOutputList.length > 0 && tempOutputList[0].output
          ? Object.keys(tempOutputList[0].output).map(t => Number(t)).sort((a, b) => a - b) : [];

        if (typeKey === 'x') {
          this.oriOutputListX = tempOutputList; this.outputListX = this.$lodash.cloneDeep(tempOutputList);
          this.form.date = timestamps;
        } else if (typeKey === 'y') {
          this.oriOutputListY = tempOutputList; this.outputListY = this.$lodash.cloneDeep(tempOutputList);
        } else {
          this.oriOutputList = tempOutputList; this.outputList = this.$lodash.cloneDeep(tempOutputList);
          this.form.date = timestamps;
        }
      } else {
        const cleanDates = this.form.date.filter(d => d);
        if (cleanDates.length === 0) return;
        const res = await this.$axios({
          category: 'project', method: 'post',
          apiName: `${this.activeProjectId}/instrument-group/${groupId}/output`,
          data: { TIMESTAMP: cleanDates },
        });
        const outputList = res.data.sort((a, b) => a.depth - b.depth);
        if (typeKey === 'x') {
          this.oriOutputListX = outputList; this.outputListX = this.$lodash.cloneDeep(outputList);
        } else if (typeKey === 'y') {
          this.oriOutputListY = outputList; this.outputListY = this.$lodash.cloneDeep(outputList);
        } else {
          this.oriOutputList = outputList; this.outputList = this.$lodash.cloneDeep(outputList);
        }
      }
    },
    activeCompute() {
      this.computeFlag = !this.computeFlag;

      if (this.computeFlag) {        
        if (this.computeDate) {
          this.compute();
        } else {
          this.$message.info('請先選擇基準時間點');
        }
      } else {
        this.compute();
      }
    },
    async compute() {
      // 如果關閉比較，或是沒有選擇基準點，就直接將數據還原為原始狀態（不打折、不相減）
      if (!this.computeFlag || !this.computeDate) {
        if (this.projectId && this.isMergedGroup) {
          this.outputListX = this.$lodash.cloneDeep(this.oriOutputListX);
          this.outputListY = this.$lodash.cloneDeep(this.oriOutputListY);
        } else {
          this.outputList = this.$lodash.cloneDeep(this.oriOutputList);
        }
        this.handleChange();
        return;
      }

      this.loading = true;
      try {
        if (this.projectId && this.isMergedGroup) {
          // 同時對 X 軸與 Y 軸進行基準點扣除運算
          await Promise.all([
            this.computeProcess(this.currentSelectedGroup.xGroup._id, this.oriOutputListX, 'x'),
            this.computeProcess(this.currentSelectedGroup.yGroup._id, this.oriOutputListY, 'y'),
          ]);
        } else {
          await this.computeProcess(this.form.instrumentGroup, this.oriOutputList, 'normal');
        }
        this.handleChange();
      } catch (error) {
        console.error(error);
        this.$message.error('取得比較基準資料失敗');
      } finally {
        this.loading = false;
      }
    },
    async computeProcess(groupId, oriList, targetKey) {
      const res = await this.$axios({
        category: 'project', method: 'post',
        apiName: `${this.activeProjectId}/instrument-group/${groupId}/output`,
        data: { TIMESTAMP: [this.computeDate] },
      });
      if (res.data && res.data.length > 0) {
        const baseData = res.data;
        const calculated = oriList.map(ele => {
          let output = {};
          const targetBase = this.findDepth(ele.depth, baseData);
          if (targetBase && targetBase.output) {
            const baseValue = Object.values(targetBase.output)[0];
            for (let [key, value] of Object.entries(ele.output)) {
              output[key] = value - baseValue;
            }
          } else {
            output = { ...ele.output };
          }
          return { ...ele, output };
        });
        if (targetKey === 'x') this.outputListX = calculated;
        else if (targetKey === 'y') this.outputListY = calculated;
        else this.outputList = calculated;
      }
    },
    handleChange() {
      if (!this.checked || this.form.instrumentGroup === '' || this.form.date.length === 0) {
        this.showTable = false;
        return;
      }

      let selectedDate = this.form.date;
      
      // 動態建立 Table 欄位，如果是合併型，則每一天各拆為 X 與 Y 欄位
      if (this.projectId && this.isMergedGroup) {
        this.tableColumns = [{ data: 'depth', title: '深度' }];
        selectedDate.forEach(data => {
          const formattedDate = moment(parseInt(data)).format('YYYY-MM-DD HH:mm');
          this.tableColumns.push({ data: `${data}_X`, title: `${formattedDate} (X)` });
          this.tableColumns.push({ data: `${data}_Y`, title: `${formattedDate} (Y)` });
        });

        let depthMap = {};
        // 匯入 X 軸數據
        selectedDate.forEach(date => {
          this.outputListX.forEach(data => {
            if (!depthMap[data.depth]) depthMap[data.depth] = { depth: data.depth };
            depthMap[data.depth][`${date}_X`] = data.output[String(date)];
          });
          this.outputListY.forEach(data => {
            if (!depthMap[data.depth]) depthMap[data.depth] = { depth: data.depth };
            depthMap[data.depth][`${date}_Y`] = data.output[String(date)];
          });
        });

        this.hotSettings.data = Object.values(depthMap).sort((a, b) => parseFloat(b.depth) - parseFloat(a.depth));
      } else {
        // 原有單軸數據表處理邏輯
        this.tableColumns = [
          { data: 'depth', title: '深度' },
          ...selectedDate.map(data => ({ data: String(data), title: moment(parseInt(data)).format('YYYY-MM-DD HH:mm') })),
        ];
        let depthMap = {};
        selectedDate.forEach(date => {
          this.outputList.forEach(data => {
            if (!depthMap[data.depth]) depthMap[data.depth] = { depth: data.depth };
            depthMap[data.depth][String(date)] = data.output[String(date)];
          });
        });
        this.hotSettings.data = Object.values(depthMap).sort((a, b) => parseFloat(b.depth) - parseFloat(a.depth));
      }

      this.hotSettings.columns = this.tableColumns;
      this.hotSettings.colHeaders = this.tableColumns.map(column => column.title);
      this.showTable = true;
      this.updateTable();
    },
    exportToExcel() {
      if (!this.checked || this.form.instrumentGroup === '') return;
      const headerKeys = this.tableColumns.map(col => col.data);
      const headerNames = this.tableColumns.map(col => col.title);
      const dataToExport = this.hotSettings.data.map(row => {
        return headerKeys.reduce((newRow, key, index) => {
          newRow[headerNames[index]] = row[key];
          return newRow;
        }, {});
      });
      const ws = XLSX.utils.json_to_sheet(dataToExport, { header: headerNames, skipHeader: false });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '數據');
      XLSX.writeFile(wb, `export_${this.currentSelectedGroup.name}.xlsx`);
    },
    exportChartImage() {
      // 1. 如果是人工量測模式且為合併型儀器，同時導出兩張圖
      if (this.projectId && this.isMergedGroup) {
        const chartX = this.$refs.chartX;
        const chartY = this.$refs.chartY;

        if (!chartX || !chartY) {
          this.$message.warning(this.$t('No chart to export'));
          return;
        }

        try {
          const timestampStr = moment().format('YYYYMMDD_HHmmss');
          
          // 導出 X 軸
          const dataURL_X = chartX.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#FFFFFF' });
          const linkX = document.createElement('a');
          linkX.href = dataURL_X;
          linkX.download = `${this.currentSelectedGroup.name}-X_${timestampStr}.png`;
          linkX.click();

          // 延遲一下下再觸發 Y 軸下載，避免瀏覽器同時觸發多檔下載被阻擋
          setTimeout(() => {
            const dataURL_Y = chartY.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#FFFFFF' });
            const linkY = document.createElement('a');
            linkY.href = dataURL_Y;
            linkY.download = `${this.currentSelectedGroup.name}-Y_${timestampStr}.png`;
            linkY.click();
          }, 300);

        } catch (error) {
          console.error('Export merged images failed:', error);
          this.$message.error('導出雙軸圖片失敗');
        }

      } else {
        const activeChart = this.isMergedGroup ? this.$refs.chartX : this.$refs.chart;
        if (!activeChart) return this.$message.warning(this.$t('No chart to export'));
        
        const link = document.createElement('a');
        link.href = activeChart.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#FFFFFF' });
        link.download = `${this.currentSelectedGroup.name}_${moment().format('YYYYMMDD_HHmmss')}.png`;
        link.click();
      }
    },
    findDepth(depth, datas) {
      for (let i in datas) {
        if (depth == datas[i].depth) return datas[i];
      }
      return -1;
    },
    onAddNewDate() {
      this.form.date.push(moment().startOf('day').format('x'));
    },
    getInstrumentGroupList() {
      this.$store.dispatch('data/getProjectInstrumentGroupList', { projectId: this.activeProjectId })
        .then(res => { this.instrumentGroupList = res; });
    },
    addSeven() {
      let start = moment.unix(this.form.date[0] / 1000);
      this.form.date = [start.startOf('day').format('x')];
      for (let i = 0; i < 6; i++) {
        start = start.subtract(1, 'd');
        this.form.date.push(start.startOf('day').format('x'));
      }
      this.getInstrumentGroupOutputByTime();
    },
    resizeFunc() {
      debounce(this.handleResize, 50);
    },
    updateTable() {
      setTimeout(() => {
        if (this.$refs.hotTable) this.$refs.hotTable.hotInstance.loadData(this.hotSettings.data);
      }, 100);
    },
  },
};
</script>

<style lang="stylus" scoped>
.analysis-container
  background-color: #f5f7fa
  min-height: 100vh

.shadow-sm
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.05) !important
  border: none
  border-radius: 8px

.w-100
  width: 100%

.flex-row
  display: flex
  flex-direction: row

.align-center
  align-items: center

.justify-between
  justify-content: space-between

.wrap
  flex-wrap: wrap

.setting-label
  font-size: 12px
  color: #606266
  margin: 0 0 4px 0

.modern-hot-table
  border-radius: 4px
  overflow: hidden

.chart-card
  transition: all 0.3s
  &:hover
    box-shadow: 0 4px 20px 0 rgba(0,0,0,0.1) !important

.el-form-item
  margin-bottom: 0

.p-16 { padding: 16px; }
.mb-16 { margin-bottom: 16px; }
.mr-8 { margin-right: 8px; }
.mr-16 { margin-right: 16px; }
.my-12 { margin-top: 12px; margin-bottom: 12px; }
</style>